import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../components/AppIcon";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import Modal from "../components/ui/Modal";
import {
  listBusinessInterviewTypes,
  listBusinessTopics,
  createBusinessJob,
  listQuestionBank,
} from "../api/client";

// JobCreate — authors a new opening as a DRAFT, together with its interview
// rounds, in one request. A "single" job has exactly one round (one AI
// interview); a "pipeline" chains up to 20 rounds that the daily scheduler
// advances. Round interview types are restricted to the canonical prepxai
// catalog (loaded here), matching the backend's validation. After creating a
// draft the recruiter lands on the job detail page to add candidates + activate.

const DIFFICULTIES = ["Easy", "Medium", "Hard"];

const ADVANCE_RULES = [
  { value: "cutoff", label: "Score cutoff — advance everyone at or above a score" },
  { value: "top_n", label: "Top N — advance the N highest scorers" },
  { value: "top_percent", label: "Top % — advance the top percentage" },
  { value: "none", label: "Advance everyone (no filter)" },
];

const selectClass =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

const textareaClass =
  "flex min-h-[72px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

// A blank round with the backend's own defaults so the echoed job matches.
function blankRound(n) {
  return {
    name: `Round ${n}`,
    interviewType: "",
    durationMinutes: 30,
    difficulty: "Medium",
    topics: [],
    instructions: "",
    advanceRuleType: "cutoff",
    advanceRuleValue: 60,
    maxAttempts: "", // blank = inherit job default
    manualGate: false,
    bankQuestionIds: [], // ids from this org's question bank, asked first (seed-first)
  };
}

// Convert a datetime-local value ("2026-08-30T18:00") to an RFC3339 UTC string.
function toRFC3339(localValue) {
  if (!localValue) return undefined;
  const d = new Date(localValue);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

function SectionCard({ icon, title, description, children, actions }) {
  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          {icon && (
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Icon name={icon} size={18} />
            </span>
          )}
          <div>
            <h2 className="text-base font-semibold text-foreground">{title}</h2>
            {description && <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>}
          </div>
        </div>
        {actions}
      </div>
      <div className="mt-5">{children}</div>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium leading-none text-foreground">
        {label}
        {required && <span className="ml-1 text-destructive">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function topicNameOf(topic) {
  if (!topic) return "";
  if (typeof topic === "string") return topic;
  return topic.name || topic.Name || "";
}

function topicTypeOf(topic) {
  if (!topic || typeof topic === "string") return "";
  return topic.interview_type || topic.interviewType || topic.type || topic.Type || "";
}

function normalizeTopicsPayload(value) {
  const rows = Array.isArray(value?.topics) ? value.topics : Array.isArray(value) ? value : [];
  return rows
    .map((topic) => ({ name: topicNameOf(topic), type: topicTypeOf(topic) }))
    .filter((topic) => topic.name);
}

function uniqueSorted(items) {
  return [...new Set(items.filter(Boolean))].sort((a, b) => a.localeCompare(b));
}

function MultiSelect({ options, selected, onChange, placeholder = "Select options" }) {
  const [open, setOpen] = useState(false);
  const selectedSet = useMemo(() => new Set(selected || []), [selected]);
  const label = selected?.length ? selected.join(", ") : placeholder;

  const toggle = (option) => {
    const next = selectedSet.has(option)
      ? (selected || []).filter((item) => item !== option)
      : [...(selected || []), option];
    onChange(next);
  };

  return (
    <div className="relative">
      <button
        type="button"
        className={selectClass + " justify-between text-left"}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={(selected?.length ? "text-foreground" : "text-muted-foreground") + " truncate"}>
          {label}
        </span>
        <Icon name="ChevronDown" size={15} className="ml-2 flex-shrink-0 text-muted-foreground" />
      </button>
      {open && (
        <>
          <div className="absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-lg">
            {options.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">No options available</div>
            ) : (
              options.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => toggle(option)}
                  className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
                >
                  <span className="truncate">{option}</span>
                  {selectedSet.has(option) && <Icon name="Check" size={14} className="text-primary" />}
                </button>
              ))
            )}
          </div>
          <button
            type="button"
            aria-label="Close selector"
            className="fixed inset-0 z-20 cursor-default"
            onClick={() => setOpen(false)}
          />
        </>
      )}
    </div>
  );
}

function bankQuestionTitle(q) {
  const t = q?.problemStatement?.title;
  return (typeof t === "string" && t.trim()) || "Untitled question";
}

// RoundQuestionPicker lets a recruiter choose bank questions for one round. It
// preserves selection ORDER (seed-first asks them in the order picked) and, by
// default, narrows the list to questions whose type matches the round — the
// interview engine tops up the rest of the time with AI-selected questions.
function RoundQuestionPicker({ open, round, allQuestions, onClose, onSave }) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState("");
  const [matchTypeOnly, setMatchTypeOnly] = useState(true);

  const roundType = round?.interviewType || "";

  // Seed local selection from the round each time the picker opens.
  useEffect(() => {
    if (open) {
      setSelectedIds(Array.isArray(round?.bankQuestionIds) ? [...round.bankQuestionIds] : []);
      setSearch("");
      setMatchTypeOnly(!!roundType);
    }
  }, [open, round, roundType]);

  const toggle = (id) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const visible = useMemo(() => {
    const s = search.trim().toLowerCase();
    return (allQuestions || []).filter((q) => {
      if (matchTypeOnly && roundType && q.type !== roundType) return false;
      if (!s) return true;
      const title = bankQuestionTitle(q).toLowerCase();
      const desc = String(q?.problemStatement?.description || "").toLowerCase();
      return title.includes(s) || desc.includes(s);
    });
  }, [allQuestions, matchTypeOnly, roundType, search]);

  const selectClassLocal =
    "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Ask from question bank"
      description="Chosen questions are asked first, in order; prepxai's AI fills the remaining time. Leave empty for a fully AI-driven interview."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => onSave(selectedIds)} iconName="Check">
            Use {selectedIds.length > 0 ? `${selectedIds.length} ` : ""}question
            {selectedIds.length === 1 ? "" : "s"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <input
            className={selectClassLocal + " flex-1"}
            placeholder="Search your bank…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {roundType && (
            <label className="flex flex-shrink-0 items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring"
                checked={matchTypeOnly}
                onChange={(e) => setMatchTypeOnly(e.target.checked)}
              />
              Only {roundType}
            </label>
          )}
        </div>

        <div className="max-h-[45vh] space-y-2 overflow-y-auto pr-1">
          {visible.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
              {(allQuestions || []).length === 0
                ? "Your question bank is empty. Add questions from the Question Bank page first."
                : "No bank questions match. Try clearing the type filter or search."}
            </div>
          ) : (
            visible.map((q) => {
              const idx = selectedIds.indexOf(q.id);
              const checked = idx !== -1;
              return (
                <label
                  key={q.id}
                  className={
                    "flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors " +
                    (checked
                      ? "border-primary/50 bg-primary/5"
                      : "border-border hover:border-primary/30 hover:bg-muted/30")
                  }
                >
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring"
                    checked={checked}
                    onChange={() => toggle(q.id)}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {checked && (
                        <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
                          {idx + 1}
                        </span>
                      )}
                      <span className="truncate text-sm font-medium text-foreground">
                        {bankQuestionTitle(q)}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span className="rounded bg-muted px-1.5 py-0.5">{q.type}</span>
                      {q.difficulty && <span>{q.difficulty}</span>}
                      {q.expectedTimeMinutes != null && <span>{q.expectedTimeMinutes} min</span>}
                    </div>
                  </div>
                </label>
              );
            })
          )}
        </div>
      </div>
    </Modal>
  );
}

export default function JobCreate() {
  const navigate = useNavigate();

  const [interviewTypes, setInterviewTypes] = useState([]);
  const [topics, setTopics] = useState([]);
  const [typesError, setTypesError] = useState("");

  const [mode, setMode] = useState("single");
  const [title, setTitle] = useState("");
  const [department, setDepartment] = useState("");
  const [location, setLocation] = useState("");
  const [deadline, setDeadline] = useState("");
  const [dailyRunHour, setDailyRunHour] = useState(18);
  const [defaultMaxAttempts, setDefaultMaxAttempts] = useState(1);
  const [defaultManualGate, setDefaultManualGate] = useState(false);
  const [rounds, setRounds] = useState([blankRound(1)]);

  // The org's question bank (loaded once) powers per-round "Ask from question
  // bank". pickerRound holds the index of the round whose picker is open.
  const [bankQuestions, setBankQuestions] = useState([]);
  const [pickerRound, setPickerRound] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [typesData, topicsData] = await Promise.all([
          listBusinessInterviewTypes(),
          listBusinessTopics(),
        ]);
        if (!alive) return;
        const names = (typesData.interviewTypes || []).map((t) => t.name);
        setInterviewTypes(typesData.interviewTypes || []);
        setTopics(normalizeTopicsPayload(topicsData));
        // Default the first round's type to the first catalog entry for convenience.
        if (names.length) {
          setRounds((prev) =>
            prev.map((r, i) => (i === 0 && !r.interviewType ? { ...r, interviewType: names[0] } : r))
          );
        }
      } catch (err) {
        if (alive) setTypesError(err?.error || "Could not load interview setup options.");
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Load the org's question bank once so each round can seed from it. Non-fatal:
  // a failure just leaves the picker empty (recruiter can still run AI-only).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await listQuestionBank();
        if (alive) setBankQuestions(data.questions || []);
      } catch {
        /* ignore — bank selection is optional */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const bankById = useMemo(() => {
    const m = new Map();
    for (const q of bankQuestions) m.set(q.id, q);
    return m;
  }, [bankQuestions]);

  // Switching to single mode collapses to a single round.
  const switchMode = (next) => {
    setMode(next);
    if (next === "single") {
      setRounds((prev) => prev.slice(0, 1));
    }
  };

  const updateRound = useCallback((idx, patch) => {
    setRounds((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }, []);

  const addRound = () => {
    setRounds((prev) => {
      if (prev.length >= 20) return prev;
      const next = blankRound(prev.length + 1);
      // Inherit the previous round's interview type as a sensible starting point.
      const firstType = interviewTypes[0]?.name || "";
      next.interviewType = prev[prev.length - 1]?.interviewType || firstType;
      return [...prev, next];
    });
  };

  const removeRound = (idx) => {
    setRounds((prev) => {
      if (prev.length <= 1) return prev;
      const next = prev.filter((_, i) => i !== idx);
      // Re-label default names that still match the "Round N" pattern.
      return next.map((r, i) =>
        /^Round \d+$/.test(r.name) ? { ...r, name: `Round ${i + 1}` } : r
      );
    });
  };

  const validationError = useMemo(() => {
    if (title.trim().length < 2) return "Enter a job title (at least 2 characters).";
    if (mode === "single" && rounds.length !== 1)
      return "A single interview must have exactly one round.";
    for (let i = 0; i < rounds.length; i++) {
      const r = rounds[i];
      if (!r.interviewType) return `Choose an interview type for round ${i + 1}.`;
      const dur = Number(r.durationMinutes);
      if (!Number.isFinite(dur) || dur <= 0)
        return `Set a duration (minutes) for round ${i + 1}.`;
    }
    return "";
  }, [title, mode, rounds]);

  const buildPayload = () => {
    const isPipeline = mode === "pipeline";
    const payload = {
      title: title.trim(),
      mode,
      department: department.trim() || undefined,
      location: location.trim() || undefined,
      deadlineAt: toRFC3339(deadline),
      defaultMaxAttempts: Number(defaultMaxAttempts) || 1,
    };
    if (isPipeline) {
      payload.dailyRunHour = Number(dailyRunHour);
      payload.defaultManualGate = !!defaultManualGate;
    }
    payload.rounds = rounds.map((r, i) => {
      const isFinal = i === rounds.length - 1;
      const round = {
        name: r.name.trim() || `Round ${i + 1}`,
        interviewType: r.interviewType,
        durationMinutes: Number(r.durationMinutes),
        difficulty: r.difficulty,
        topics: Array.isArray(r.topics) ? r.topics : [],
        instructions: r.instructions.trim() || undefined,
      };
      // Advancement only applies to a pipeline's non-final rounds (there is no
      // "next round" after the last one, and single mode has none at all).
      if (isPipeline && !isFinal) {
        round.advanceRuleType = r.advanceRuleType;
        if (r.advanceRuleType !== "none") {
          round.advanceRuleValue = Number(r.advanceRuleValue) || 0;
        }
        round.manualGate = !!r.manualGate;
      }
      if (r.maxAttempts !== "" && Number(r.maxAttempts) > 0) {
        round.maxAttempts = Number(r.maxAttempts);
      }
      // Seed-first: bank questions the recruiter picked are asked before AI
      // selection. The backend validates each id belongs to this org's bank.
      if (Array.isArray(r.bankQuestionIds) && r.bankQuestionIds.length > 0) {
        round.questionIds = r.bankQuestionIds;
      }
      return round;
    });
    return payload;
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmitting(true);
    try {
      const job = await createBusinessJob(buildPayload());
      navigate(`/business/jobs/${job.id}`, { replace: true });
    } catch (err) {
      let msg = err?.error || "Could not create the job.";
      if (err?.allowedTypes?.length) {
        msg += ` Allowed types: ${err.allowedTypes.join(", ")}.`;
      }
      setError(msg);
      setSubmitting(false);
    }
  };

  const typeOptions = interviewTypes.map((t) => t.name);
  const topicOptionsForType = useCallback(
    (type) => {
      const normalizedType = String(type || "").trim().toLowerCase();
      const typed = topics.filter((topic) => {
        const topicType = String(topic.type || "").trim().toLowerCase();
        return !topicType || !normalizedType || topicType === normalizedType;
      });
      return uniqueSorted((typed.length ? typed : topics).map((topic) => topic.name));
    },
    [topics]
  );

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate("/business/jobs")}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <Icon name="ArrowLeft" size={15} /> Back to jobs
        </button>
        <h1 className="mt-3 text-2xl font-semibold text-foreground">Create a job</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Define the opening and the interview each candidate will take. You can review
          and activate it on the next screen.
        </p>
      </div>

      {typesError && (
        <div className="flex items-center gap-2 rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning">
          <Icon name="AlertTriangle" size={16} />
          <span>{typesError} You can still fill out the form, but the type picker may be empty.</span>
        </div>
      )}

      <form onSubmit={submit} className="space-y-6">
        {/* Mode */}
        <SectionCard
          icon="Route"
          title="Interview format"
          description="Choose a single interview or a multi-round pipeline."
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {[
              {
                key: "single",
                icon: "MessageSquare",
                title: "Single interview",
                desc: "One AI interview per candidate. Scores are shown to your team.",
              },
              {
                key: "pipeline",
                icon: "GitBranch",
                title: "Multi-round pipeline",
                desc: "Chain rounds with automatic advancement across a deadline.",
              },
            ].map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => switchMode(opt.key)}
                className={
                  "flex flex-col gap-1.5 rounded-xl border p-4 text-left transition-colors " +
                  (mode === opt.key
                    ? "border-primary bg-primary/5 ring-1 ring-primary"
                    : "border-border hover:border-primary/40 hover:bg-muted/30")
                }
              >
                <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  <Icon name={opt.icon} size={16} className="text-primary" />
                  {opt.title}
                </span>
                <span className="text-xs text-muted-foreground">{opt.desc}</span>
              </button>
            ))}
          </div>
        </SectionCard>

        {/* Job details */}
        <SectionCard icon="Briefcase" title="Job details">
          <div className="space-y-4">
            <Input
              label="Job title"
              required
              placeholder="e.g. Backend Engineer"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={160}
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Department"
                placeholder="e.g. Engineering"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
              />
              <Input
                label="Location"
                placeholder="e.g. Remote / Bangalore"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field
                label="Deadline"
                hint={
                  mode === "pipeline"
                    ? "Required to activate a pipeline — rounds are spread across this window."
                    : "Optional. Candidates can be given a due date when invited."
                }
              >
                <input
                  type="datetime-local"
                  className={selectClass}
                  value={deadline}
                  onChange={(e) => setDeadline(e.target.value)}
                />
              </Field>
              <Field
                label="Attempts per candidate"
                hint="How many times a candidate may retake a round (default 1)."
              >
                <input
                  type="number"
                  min="1"
                  max="10"
                  className={selectClass}
                  value={defaultMaxAttempts}
                  onChange={(e) => setDefaultMaxAttempts(e.target.value)}
                />
              </Field>
            </div>

            {mode === "pipeline" && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field
                  label="Daily advance time"
                  hint="The hour (0–23) each day when the scheduler advances rounds."
                >
                  <input
                    type="number"
                    min="0"
                    max="23"
                    className={selectClass}
                    value={dailyRunHour}
                    onChange={(e) => setDailyRunHour(e.target.value)}
                  />
                </Field>
                <Field label="Recruiter review" hint="Pause each round for manual sign-off before advancing.">
                  <label className="flex h-10 items-center gap-2.5 rounded-md border border-input bg-background px-3 text-sm text-foreground">
                    <Input
                      type="checkbox"
                      checked={defaultManualGate}
                      onChange={(e) => setDefaultManualGate(e.target.checked)}
                    />
                    Require my approval between rounds
                  </label>
                </Field>
              </div>
            )}
          </div>
        </SectionCard>

        {/* Rounds */}
        <SectionCard
          icon="ListChecks"
          title={mode === "single" ? "The interview" : "Pipeline rounds"}
          description={
            mode === "single"
              ? "Configure the single interview each candidate will take."
              : "Rounds run in order. Each round's rule decides who advances to the next."
          }
          actions={
            mode === "pipeline" && rounds.length < 20 ? (
              <Button type="button" variant="outline" size="sm" iconName="Plus" onClick={addRound}>
                Add round
              </Button>
            ) : null
          }
        >
          <div className="space-y-4">
            {rounds.map((r, idx) => {
              const isFinal = idx === rounds.length - 1;
              const showAdvance = mode === "pipeline" && !isFinal;
              return (
                <div key={idx} className="rounded-xl border border-border bg-background p-4">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                        {idx + 1}
                      </span>
                      {mode === "single" ? "Interview" : `Round ${idx + 1}`}
                      {mode === "pipeline" && isFinal && (
                        <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                          Final
                        </span>
                      )}
                    </span>
                    {mode === "pipeline" && rounds.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeRound(idx)}
                        className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-destructive"
                      >
                        <Icon name="Trash2" size={13} /> Remove
                      </button>
                    )}
                  </div>

                  <div className="mt-4 space-y-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <Input
                        label="Round name"
                        placeholder={`Round ${idx + 1}`}
                        value={r.name}
                        onChange={(e) => updateRound(idx, { name: e.target.value })}
                      />
                      <Field label="Interview type" required>
                        <select
                          className={selectClass}
                          value={r.interviewType}
                          onChange={(e) => updateRound(idx, { interviewType: e.target.value, topics: [] })}
                        >
                          <option value="" disabled>
                            Select a type…
                          </option>
                          {typeOptions.map((name) => (
                            <option key={name} value={name}>
                              {name}
                            </option>
                          ))}
                        </select>
                      </Field>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                      <Field label="Duration (min)" required>
                        <input
                          type="number"
                          min="1"
                          className={selectClass}
                          value={r.durationMinutes}
                          onChange={(e) => updateRound(idx, { durationMinutes: e.target.value })}
                        />
                      </Field>
                      <Field label="Difficulty">
                        <select
                          className={selectClass}
                          value={r.difficulty}
                          onChange={(e) => updateRound(idx, { difficulty: e.target.value })}
                        >
                          {DIFFICULTIES.map((d) => (
                            <option key={d} value={d}>
                              {d}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Input
                        label="Attempts (override)"
                        type="number"
                        min="1"
                        max="10"
                        placeholder="Job default"
                        value={r.maxAttempts}
                        onChange={(e) => updateRound(idx, { maxAttempts: e.target.value })}
                      />
                    </div>

                    <Field label="Topics (optional)">
                      <MultiSelect
                        options={topicOptionsForType(r.interviewType)}
                        selected={r.topics}
                        onChange={(items) => updateRound(idx, { topics: items })}
                        placeholder="Select topics"
                      />
                    </Field>

                    <Field label="Instructions for the interviewer (optional)">
                      <textarea
                        className={textareaClass}
                        placeholder="Any focus areas or guidance for this round…"
                        value={r.instructions}
                        onChange={(e) => updateRound(idx, { instructions: e.target.value })}
                      />
                    </Field>

                    <Field
                      label="Ask from question bank (optional)"
                      hint="Selected questions are asked first, in order; prepxai's AI fills the remaining time."
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          iconName="Library"
                          onClick={() => setPickerRound(idx)}
                        >
                          {r.bankQuestionIds.length > 0
                            ? `${r.bankQuestionIds.length} selected`
                            : "Select questions"}
                        </Button>
                        {r.bankQuestionIds.length > 0 && (
                          <button
                            type="button"
                            onClick={() => updateRound(idx, { bankQuestionIds: [] })}
                            className="text-xs font-medium text-muted-foreground hover:text-destructive"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      {r.bankQuestionIds.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {r.bankQuestionIds.map((qid, qi) => {
                            const q = bankById.get(qid);
                            return (
                              <span
                                key={qid}
                                className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary"
                                title={q ? bankQuestionTitle(q) : qid}
                              >
                                <span className="font-semibold">{qi + 1}.</span>
                                <span className="max-w-[180px] truncate">
                                  {q ? bankQuestionTitle(q) : "Question"}
                                </span>
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateRound(idx, {
                                      bankQuestionIds: r.bankQuestionIds.filter((x) => x !== qid),
                                    })
                                  }
                                  className="ml-0.5 rounded-full p-0.5 hover:bg-primary/20"
                                  aria-label="Remove question"
                                >
                                  <Icon name="X" size={11} />
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </Field>

                    {showAdvance && (
                      <div className="rounded-lg border border-dashed border-border bg-muted/30 p-4">
                        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          <Icon name="Filter" size={13} /> Who advances to the next round
                        </div>
                        <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <Field label="Rule">
                            <select
                              className={selectClass}
                              value={r.advanceRuleType}
                              onChange={(e) => updateRound(idx, { advanceRuleType: e.target.value })}
                            >
                              {ADVANCE_RULES.map((rule) => (
                                <option key={rule.value} value={rule.value}>
                                  {rule.label}
                                </option>
                              ))}
                            </select>
                          </Field>
                          {r.advanceRuleType !== "none" && (
                            <Field
                              label={
                                r.advanceRuleType === "cutoff"
                                  ? "Minimum score (0–100)"
                                  : r.advanceRuleType === "top_percent"
                                  ? "Top percentage"
                                  : "Number of candidates"
                              }
                            >
                              <input
                                type="number"
                                min="0"
                                className={selectClass}
                                value={r.advanceRuleValue}
                                onChange={(e) =>
                                  updateRound(idx, { advanceRuleValue: e.target.value })
                                }
                              />
                            </Field>
                          )}
                        </div>
                        <label className="mt-3 flex items-center gap-2.5 text-sm text-foreground">
                          <Input
                            type="checkbox"
                            checked={r.manualGate}
                            onChange={(e) => updateRound(idx, { manualGate: e.target.checked })}
                          />
                          Pause for my review before advancing this round
                        </label>
                      </div>
                    )}

                    {mode === "pipeline" && isFinal && (
                      <p className="flex items-center gap-1.5 rounded-lg bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                        <Icon name="Trophy" size={13} className="text-primary" />
                        Candidates who clear this round are your finalists.
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>

        {/* Error + submit */}
        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            <Icon name="AlertCircle" size={16} className="mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex items-center justify-end gap-3">
          <Button type="button" variant="ghost" onClick={() => navigate("/business/jobs")}>
            Cancel
          </Button>
          <Button
            type="submit"
            iconName="Check"
            loading={submitting}
            disabled={submitting || !!validationError}
          >
            Create draft
          </Button>
        </div>
      </form>

      <RoundQuestionPicker
        open={pickerRound !== null && pickerRound < rounds.length}
        round={pickerRound !== null ? rounds[pickerRound] : null}
        allQuestions={bankQuestions}
        onClose={() => setPickerRound(null)}
        onSave={(ids) => {
          if (pickerRound !== null) updateRound(pickerRound, { bankQuestionIds: ids });
          setPickerRound(null);
        }}
      />
    </div>
  );
}
